/**
 * Cart Upgrade Upsell
 * Handles the cart swap logic for upgrade offers.
 * Works in both cart drawer and cart page contexts.
 */
(function () {
  'use strict';

  const DISMISS_KEY = 'cart_upgrade_dismissed';
  const ACCEPT_KEY = 'cart_upgrade_accepted';

  // Variant IDs for the $0 "Complimentary Gift Card" dummy product (Draft, product ID 7646099964000)
  const GIFT_CARD_BONUS_VARIANTS = {
    50: 42580477739104,
    100: 42580478525536,
    250: 42580478558304,
    500: 42580478591072
  };

  var _cleanupInProgress = false;
  var _lastCleanupTime = 0;

  /**
   * Check if offer was dismissed this session
   */
  function isDismissed() {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  /**
   * Check if an upgrade was already accepted this session
   */
  function isAccepted() {
    try {
      return sessionStorage.getItem(ACCEPT_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark offer as dismissed for this session
   */
  function setDismissed() {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      // sessionStorage unavailable
    }
  }

  /**
   * Check if element is inside the cart drawer
   */
  function isInDrawer(el) {
    return !!el.closest('cart-drawer');
  }

  /**
   * Refresh the cart drawer via its custom event
   */
  function refreshDrawer() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
  }

  /**
   * Refresh the cart page by re-fetching the main-cart section
   */
  async function refreshCartPage() {
    try {
      const res = await fetch('/?sections=main-cart');
      const sections = await res.json();
      const html = sections['main-cart'];
      if (!html) return;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const newItems = tmpl.content.querySelector('.cart__items');
      const currentItems = document.querySelector('.cart__items');
      if (newItems && currentItems) {
        currentItems.innerHTML = newItems.innerHTML;
      }

      // Also update cart summary
      const newSummary = tmpl.content.querySelector('#cart-summary');
      const currentSummary = document.querySelector('#cart-summary');
      if (newSummary && currentSummary) {
        currentSummary.innerHTML = newSummary.innerHTML;
      }
    } catch (e) {
      // Fallback: reload page
      window.location.reload();
    }
  }

  /**
   * Update the cart icon bubble count
   */
  async function refreshCartBubble() {
    try {
      const res = await fetch('/?sections=cart-icon-bubble');
      const sections = await res.json();
      const html = sections['cart-icon-bubble'];
      if (!html) return;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const newBubble = tmpl.content.querySelector('.shopify-section');
      const currentBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    } catch (e) {
      // Non-critical
    }
  }

  /**
   * Check if the banner's triggering line item still exists in cart.
   * Hides banner if the product was removed.
   */
  async function checkBannerValidity() {
    var banner = document.querySelector('.cart-upgrade-upsell:not([hidden])');
    if (!banner) return;

    var lineKey = banner.dataset.lineKey;
    if (!lineKey) return;

    try {
      var res = await fetch('/cart.js');
      var cart = await res.json();
      var exists = cart.items.some(function (item) {
        return item.key === lineKey;
      });
      if (!exists) {
        banner.hidden = true;
      }
    } catch (e) {
      // Non-critical
    }
  }

  /**
   * Perform the cart swap: add upgrade, remove original
   */
  async function performUpgrade(card) {
    const lineKey = card.dataset.lineKey;
    const upgradeVariantId = card.dataset.upgradeVariantId;
    const giftCardAmount = card.dataset.giftCardAmount;
    const btn = card.querySelector('.cart-upgrade-upsell__btn');
    const inDrawer = isInDrawer(card);

    if (!lineKey || !upgradeVariantId) return;

    // Disable button, show loading state
    var btnOriginalText = btn.textContent;
    btn.textContent = 'Upgrading...';
    btn.disabled = true;
    card.classList.add('cart-upgrade-upsell--upgrading');

    try {
      // Step 1: Add upgrade variant with hidden line item properties
      console.log('[CartUpgrade] Step 1: Adding upgrade variant', upgradeVariantId);
      const properties = {
        _cart_upgrade: 'true'
      };
      if (giftCardAmount && parseFloat(giftCardAmount) > 0) {
        properties._cart_upgrade_gift_card = giftCardAmount;
        properties['Gift Card Bonus'] = '$' + parseFloat(giftCardAmount).toFixed(2);
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(upgradeVariantId, 10),
            quantity: 1,
            properties: properties
          }]
        })
      });

      if (!addRes.ok) {
        throw new Error('Failed to add upgrade item');
      }
      console.log('[CartUpgrade] Step 1 complete: Upgrade variant added');

      // Step 2: Remove original item
      console.log('[CartUpgrade] Step 2: Removing original item', lineKey);
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      if (!changeRes.ok) {
        throw new Error('Failed to remove original item');
      }
      console.log('[CartUpgrade] Step 2 complete: Original item removed');

      // Step 2.5: Add gift card bonus line item (non-blocking)
      if (giftCardAmount && parseFloat(giftCardAmount) > 0) {
        console.log('[CartUpgrade] Step 2.5: Adding gift card bonus, amount:', giftCardAmount);
        try {
          var gcVariantId = GIFT_CARD_BONUS_VARIANTS[parseInt(giftCardAmount, 10)];
          if (gcVariantId) {
            var gcRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [{
                  id: gcVariantId,
                  quantity: 1,
                  properties: {
                    _gift_card_bonus: 'true',
                    _linked_upgrade: upgradeVariantId
                  }
                }]
              })
            });
            if (!gcRes.ok) {
              console.warn('[CartUpgrade] Gift card bonus add failed:', gcRes.status);
            } else {
              console.log('[CartUpgrade] Step 2.5 complete: Gift card bonus added');
            }
          } else {
            console.warn('[CartUpgrade] No gift card variant found for amount:', giftCardAmount);
          }
        } catch (gcErr) {
          console.warn('[CartUpgrade] Gift card bonus error (non-fatal):', gcErr);
        }
      }

      // Step 3: Refresh the appropriate cart UI
      console.log('[CartUpgrade] Step 3: Refreshing cart UI, inDrawer:', inDrawer);
      if (inDrawer) {
        refreshDrawer();
      } else {
        await refreshCartPage();
      }

      // Step 4: Update cart icon bubble
      refreshCartBubble();

      // Step 5: Mark upgrade as accepted so no further offers show
      try {
        sessionStorage.setItem(ACCEPT_KEY, '1');
      } catch (e) {
        // sessionStorage unavailable
      }
      console.log('[CartUpgrade] Upgrade complete');

    } catch (err) {
      // Restore button on error
      btn.textContent = btnOriginalText || 'Upgrade';
      btn.disabled = false;
      card.classList.remove('cart-upgrade-upsell--upgrading');
      console.error('[CartUpgrade] Cart upgrade failed:', err);
    }
  }

  /**
   * Initialize: hide if dismissed, bind events via delegation
   */
  function init() {
    // Hide all upgrade cards if dismissed or already accepted this session
    if (isDismissed() || isAccepted()) {
      document.querySelectorAll('.cart-upgrade-upsell').forEach(function (el) {
        el.hidden = true;
      });
    }

    // Event delegation on document (survives DOM replacement)
    document.addEventListener('click', function (e) {
      // Upgrade button click
      var btn = e.target.closest('.cart-upgrade-upsell__btn');
      if (btn) {
        var card = btn.closest('.cart-upgrade-upsell');
        if (card) {
          performUpgrade(card);
        }
        return;
      }

      // Dismiss button click
      var dismiss = e.target.closest('.cart-upgrade-upsell__dismiss');
      if (dismiss) {
        var card = dismiss.closest('.cart-upgrade-upsell');
        if (card) {
          card.hidden = true;
          setDismissed();
        }
        return;
      }

      // Cart item remove button click
      var removeBtn = e.target.closest('.cart-item__remove, [data-cart-remove], .cart__remove, .js-remove-item');
      if (removeBtn) {
        console.log('[CartUpgrade] Remove button clicked (click handler)');
        setTimeout(checkBannerValidity, 800);
        setTimeout(function () {
          console.log('[CartUpgrade] Click handler triggering cleanup');
          cleanupOrphanedGiftCards();
        }, 1500);
        return;
      }
    });
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  console.log('[CartUpgrade] Script loaded, listeners registered');

  // On page load, check for orphaned gift cards (catches full-page-navigation removals on /cart)
  setTimeout(function () {
    console.log('[CartUpgrade] Page-load orphan check');
    cleanupOrphanedGiftCards();
  }, 1000);

  // After drawer refreshes, check dismissed/accepted state AND banner validity
  document.addEventListener('dispatch:cart-drawer:refresh', function () {
    setTimeout(function () {
      if (isDismissed() || isAccepted()) {
        document.querySelectorAll('.cart-upgrade-upsell').forEach(function (el) {
          el.hidden = true;
        });
      }
      checkBannerValidity();
    }, 500);
  });

  // Listen for native cart item changes (capture phase to see event even if propagation is stopped)
  document.addEventListener('on:line-item:change', function (e) {
    var detail = e.detail || {};
    console.log('[CartUpgrade] on:line-item:change fired, newQuantity:', detail.newQuantity, 'variantId:', detail.variantId);

    // Banner visibility check
    if (detail.cart && detail.cart.items) {
      var banner = document.querySelector('.cart-upgrade-upsell:not([hidden])');
      if (banner && banner.dataset.lineKey) {
        var exists = detail.cart.items.some(function (item) {
          return item.key === banner.dataset.lineKey;
        });
        if (!exists) banner.hidden = true;
      }
    }

    // Only cleanup on full removal (quantity = 0)
    if (detail.newQuantity !== undefined && detail.newQuantity !== 0) return;

    // Skip if the removed item is a gift card itself (prevent loops)
    var gcVariantIds = Object.values(GIFT_CARD_BONUS_VARIANTS).map(String);
    if (detail.variantId && gcVariantIds.indexOf(String(detail.variantId)) !== -1) {
      console.log('[CartUpgrade] Removed item is a gift card, skipping cleanup');
      return;
    }

    console.log('[CartUpgrade] Event listener triggering cleanup in 500ms');
    setTimeout(cleanupOrphanedGiftCards, 500);
  }, true);

  /**
   * Remove orphaned gift card bonus items when their linked upgrade is no longer in cart.
   * Always fetches fresh cart data. Uses cartItems.refresh() for native UI updates.
   */
  async function cleanupOrphanedGiftCards() {
    if (_cleanupInProgress) {
      console.log('[CartUpgrade] Cleanup already in progress, skipping');
      return;
    }
    if (Date.now() - _lastCleanupTime < 2000) {
      console.log('[CartUpgrade] Cleanup ran recently (' + (Date.now() - _lastCleanupTime) + 'ms ago), skipping');
      return;
    }
    _cleanupInProgress = true;

    try {
      console.log('[CartUpgrade] Fetching fresh cart data...');
      var res = await fetch('/cart.js');
      var cart = await res.json();
      console.log('[CartUpgrade] Cart has', cart.items.length, 'items');

      var upgradeVariantIds = cart.items
        .filter(function (item) { return !item.properties || item.properties._gift_card_bonus !== 'true'; })
        .map(function (item) { return String(item.variant_id); });
      console.log('[CartUpgrade] Non-GC variant IDs in cart:', upgradeVariantIds);

      var orphans = cart.items.filter(function (item) {
        if (!item.properties) return false;
        var isGC = item.properties._gift_card_bonus === 'true';
        var linkedTo = item.properties._linked_upgrade;
        if (!isGC || !linkedTo) return false;
        var linkedPresent = upgradeVariantIds.indexOf(String(linkedTo)) !== -1;
        console.log('[CartUpgrade] GC item', item.key, 'linked to', linkedTo, '- present:', linkedPresent);
        return !linkedPresent;
      });

      if (orphans.length === 0) {
        console.log('[CartUpgrade] No orphans found');
        _lastCleanupTime = Date.now();
        return;
      }

      console.log('[CartUpgrade] Found', orphans.length, 'orphan(s), removing...');
      for (var i = 0; i < orphans.length; i++) {
        console.log('[CartUpgrade] Removing orphan:', orphans[i].key);
        var changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: orphans[i].key, quantity: 0 })
        });
        console.log('[CartUpgrade] Remove response status:', changeRes.status);
      }

      console.log('[CartUpgrade] Orphans removed, refreshing UI...');
      var cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl && typeof cartItemsEl.refresh === 'function') {
        console.log('[CartUpgrade] Calling cartItems.refresh()');
        cartItemsEl.refresh();
      } else if (document.querySelector('cart-drawer')) {
        console.log('[CartUpgrade] Falling back to refreshDrawer()');
        refreshDrawer();
      } else {
        console.log('[CartUpgrade] Falling back to refreshCartPage()');
        await refreshCartPage();
      }
      refreshCartBubble();
      _lastCleanupTime = Date.now();
      console.log('[CartUpgrade] Cleanup complete');
    } catch (e) {
      console.error('[CartUpgrade] Cleanup error:', e);
    } finally {
      _cleanupInProgress = false;
    }
  }

})();
